import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, type TenantSummary } from '../api';
import { useAuth } from '../auth';
import { Banner, Card, Empty, TextField, relativeTime } from '../components/ui';

export function TenantsPage() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get<TenantSummary[]>('/api/tenants').then(setTenants).catch(() => setTenants([]));
  useEffect(() => { void load(); }, []);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post('/api/tenants', { slug, name: name || slug });
      setSlug('');
      setName('');
      setCreating(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create tenant');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="row" style={{ marginBottom: 20 }}>
        <div>
          <h1 className="page-title">Tenants</h1>
          <p className="page-sub">
            Each tenant is an isolated set of connections, endpoints and signing keys,
            addressed under <code>/t/&lt;slug&gt;/</code>.
          </p>
        </div>
        <div className="spacer" />
        {user?.role === 'super_admin' && (
          <button className="primary" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : 'New tenant'}
          </button>
        )}
      </div>

      {creating && (
        <Card title="Create tenant">
          <form onSubmit={create}>
            {error && <Banner kind="error">{error}</Banner>}
            <div className="grid two">
              <TextField
                label="Slug" value={slug} onChange={(v) => setSlug(v.toLowerCase())}
                placeholder="acme" mono
                hint="Lowercase letters, digits and hyphens. Appears in every endpoint URL."
              />
              <TextField label="Display name" value={name} onChange={setName} placeholder="Acme Corp" />
            </div>
            <button className="primary" type="submit" disabled={busy || !slug}>
              {busy ? 'Creating…' : 'Create tenant'}
            </button>
            <span className="faint small" style={{ marginLeft: 10 }}>
              Generates a dedicated SP signing key — takes a second.
            </span>
          </form>
        </Card>
      )}

      {tenants === null ? (
        <p className="faint">Loading…</p>
      ) : tenants.length === 0 ? (
        <Empty>
          No tenants yet.
          {user?.role === 'super_admin' ? ' Create one to get started.' : ' Ask a super admin to grant you access.'}
        </Empty>
      ) : (
        <Card>
          <table>
            <thead>
              <tr>
                <th>Tenant</th><th>Slug</th><th>SAML</th><th>OIDC</th><th>PingAccess</th><th>Created</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id}>
                  <td>
                    <Link to={`/admin/tenants/${t.id}`}>{t.name}</Link>
                    {t.description && <div className="faint small">{t.description}</div>}
                  </td>
                  <td className="mono dim">{t.slug}</td>
                  <td>{t.counts.saml}</td>
                  <td>{t.counts.oidc}</td>
                  <td>{t.counts.ping}</td>
                  <td className="dim nowrap">{relativeTime(t.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
